import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  debounceTime,
  filter,
  fromEvent,
  map,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

import * as fromStore from 'src/app/store/';
import { Comment } from 'src/app/models/comment.model';
import { BusinessPurpose, Ticket } from 'src/app/models/ticket/ticket.model';
import { isTicketSupport } from 'src/app/models/ticket/ticket.util';
import { CommentService } from '../../services';

@Component({
  selector: 'app-tickets-detail-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['detail-shell.component.scss'],
  templateUrl: './detail-shell.component.html',
  animations: [
    trigger('descriptionAnimation', [
      state(
        'expand',
        style({
          height: '*',
          minHeight: '26px',
        }),
      ),
      state(
        'collapse',
        style({
          height: '26px',
        }),
      ),
      transition('collapse <=> expand', animate('300ms ease-in-out')),
    ]),
    trigger('detailsAnimation', [
      state(
        'expand',
        style({
          height: '*',
          opacity: '1',
        }),
      ),
      state(
        'collapse',
        style({
          height: '0',
          opacity: '0',
        }),
      ),
      transition('expand <=> collapse', animate('300ms ease-in-out')),
    ]),
  ],
  standalone: false,
})
export class TicketsDetailShellComponent
  implements AfterViewInit, OnDestroy, OnInit
{
  @Input() isLoading: boolean;

  ticket$: Observable<Ticket> = this.store.select(fromStore.getSelectedTicket);

  isTicketIdOnRouter = false;

  browserSize$: Observable<string> = this.store.select(
    fromStore.getBrowserSizeName,
  );

  browserSize: string;

  readonly allowUserInput$ = this.ticket$.pipe(
    map(
      (ticket) =>
        ticket?.Business_Purpose__c ===
        BusinessPurpose.NOCProactiveCustomerAlert,
    ),
  );

  readonly descriptionSections$ = this.ticket$.pipe(
    map((ticket) => ticket?.Description?.split('\n') ?? []),
  );

  showDetails = true;

  showDescription = false;

  descriptionState = 'collapse';

  detailState = 'expand';

  descriptionContentHeight = 0;

  descriptionHeight = 0;

  widgetHeight = 0;

  ticketDetailHeaderHeight = 0;

  isNocTicket: boolean;

  fileUploadResult = {};

  @ViewChild('descriptionElement', { static: false, read: ElementRef })
  descriptionElement: ElementRef;

  @ViewChild('ticketDetailContent', { static: false, read: ElementRef })
  ticketDetailContent: ElementRef;

  @ViewChild('ticketDetailHeader', { static: false })
  ticketDetailHeader: ElementRef;

  @ViewChild('ticketDetailWidget', { static: false, read: ElementRef })
  ticketDetailWidget: ElementRef;

  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private store: Store<fromStore.State>,
    private commentService: CommentService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Ensure tickets are loaded in the store for mobile navigation
    this.store
      .select(fromStore.getTicketsLoaded)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadTickets());
        }
      });

    // Monitor browser size changes from store
    let previousBrowserSize: string | null = null;
    this.browserSize$.pipe(takeUntil(this.destroy$)).subscribe((name) => {
      if (!name) return;

      const wasMobile = previousBrowserSize === 'small';
      const isDesktop = name !== 'small';

      // If browser changed from mobile to desktop while on direct route, redirect
      if (wasMobile && isDesktop && this.isTicketIdOnRouter) {
        this.redirectToTicketsList();
        return;
      }

      previousBrowserSize = this.browserSize;
      this.browserSize = name;
      this.cdr.detectChanges();
    });

    // Also listen to window resize events directly to catch changes immediately
    let previousWindowWidth: number | null = null;
    if (typeof window !== 'undefined') {
      previousWindowWidth = window.innerWidth;
      fromEvent(window, 'resize')
        .pipe(debounceTime(100), takeUntil(this.destroy$))
        .subscribe(() => {
          const currentWidth = window.innerWidth;
          const isMobile = currentWidth <= 766;
          const wasMobile =
            previousWindowWidth !== null && previousWindowWidth <= 766;

          // If browser changed from mobile to desktop, redirect
          if (wasMobile && !isMobile && this.isTicketIdOnRouter) {
            this.redirectToTicketsList();
          }

          previousWindowWidth = currentWidth;
        });
    }

    this.ticket$.pipe(takeUntil(this.destroy$)).subscribe((ticket) => {
      this.isNocTicket = isTicketSupport(ticket);
      this.initialize();
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    this.getDescriptionIssueHeight();
    this.getTicketHeaderHeight();
    this.getTicketDetailHeight();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initialize(): void {
    this.isTicketIdOnRouter = !!this.route.snapshot.paramMap.get('ticketId');

    if (this.browserSize === 'small') {
      this.onSmallScreen();
    } else {
      this.markCommentsAsRead();
    }
    this.fileUploadResult = { name: '', success: false };
  }

  redirectToTicketsList(): void {
    const ticketId = this.route.snapshot.paramMap.get('ticketId');
    if (ticketId) {
      this.router.navigate(['/tickets'], {
        queryParams: { ticket: ticketId },
      });
    } else {
      this.router.navigate(['/tickets']);
    }
  }

  onSmallScreen(): void {
    // Wait for tickets to be loaded before setting up the ticket observable
    this.store
      .select(fromStore.getTicketsLoaded)
      .pipe(
        filter((loaded) => loaded),
        take(1),
        switchMap(() => this.store.select(fromStore.getCurrentRouterTicket)),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((ticket) => {
        this.ticket$ = this.store.select(fromStore.getCurrentRouterTicket);
        this.cdr.detectChanges();
      });
  }

  reloadTicketsData(): void {
    this.store.dispatch(new fromStore.LoadTickets());
  }

  fileUploaded(uploadResults): void {
    this.fileUploadResult = uploadResults;
  }

  markCommentsAsRead(): void {
    this.ticket$
      .pipe(
        take(1),
        filter(
          (t): t is Ticket =>
            !!t?.Id &&
            (t.Comments__r?.totalSize ?? 0) > 0 &&
            this.hasUnreadComments(t.Comments__r?.records ?? []),
        ),
        switchMap((t) =>
          this.commentService.markCommentsAsRead({
            Case__c: t.Id,
          }),
        ),
      )
      .subscribe();
  }

  hasUnreadComments = (comments: Comment[]): boolean => {
    if (comments.length) {
      return comments.some((c) => !c.Is_Read__c);
    }
    return false;
  };

  // Get heights for smooth slide/collapse animations
  getTicketHeaderHeight(): void {
    if (this.ticketDetailHeader && this.ticketDetailHeader.nativeElement) {
      this.ticketDetailHeaderHeight =
        this.ticketDetailHeader.nativeElement.offsetHeight;
    }
  }

  getTicketDetailHeight(): void {
    if (this.ticketDetailWidget && this.ticketDetailWidget.nativeElement) {
      this.widgetHeight = this.ticketDetailWidget.nativeElement.offsetHeight;
    }
  }

  getDescriptionIssueHeight(): any {
    if (this.descriptionElement) {
      try {
        this.descriptionHeight =
          this.descriptionElement.nativeElement.offsetHeight;

        if (
          this.ticketDetailContent &&
          this.ticketDetailContent.nativeElement
        ) {
          this.descriptionContentHeight =
            this.ticketDetailContent.nativeElement.offsetHeight;
        }
      } catch (err) {
        console.error(err);
      }
    }
  }

  // States for animations
  toggleDetailState(): void {
    this.detailState = this.detailState === 'expand' ? 'collapse' : 'expand';
  }

  toggleDescriptionState(): void {
    this.descriptionState =
      this.descriptionState === 'collapse' ? 'expand' : 'collapse';
  }

  animationDone(): void {
    this.getDescriptionIssueHeight();
    this.getTicketDetailHeight();
  }
}
